import type { AccountProfile } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, StatusBadge } from '@mypoz/ui';

export interface ProfileHeaderProps {
  profile: AccountProfile;
}

/** Initials from a display name, for the plain avatar circle — no photo endpoint exists yet. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Name, VIP tier and status pill — the one part of this screen that isn't a plain label/value
 * row, so it gets its own component rather than being squeezed into ProfileField.
 *
 * The status pill isn't in the MYP-72 ticket text; the mentor mockup shows one reading "ACTIVE".
 * See the doc comment on `AccountProfile.status` — rendered as given, unconfirmed until MYP-73's
 * live check of `alipay_page2` says what field (if any) actually carries it.
 */
export function ProfileHeader({ profile }: ProfileHeaderProps) {
  const { t } = useTranslation();
  const active = profile.status === 'ACTIVE';
  // An account with no name on record is shown by its customer number rather than as a blank.
  const displayName = profile.name ?? profile.customerId;

  return (
    <Card className="flex flex-wrap items-center gap-4">
      <span
        aria-hidden="true"
        className="flex size-14 shrink-0 items-center justify-center rounded-full bg-brand-soft text-lg font-bold text-brand"
      >
        {initials(displayName)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-lg font-semibold text-ink">{displayName}</span>
        <span className="mt-0.5 block text-sm text-muted">
          {t('account.profile.vipTier', { tier: profile.vipTier })}
        </span>
      </span>

      <StatusBadge tone={active ? 'success' : 'neutral'}>
        {t(`account.profile.status.${active ? 'active' : 'inactive'}`)}
      </StatusBadge>
    </Card>
  );
}
